/**
 * Ouverture des restaurants.
 *
 * Un restaurant et son premier gerant naissent ensemble : creer l'un sans
 * l'autre laisserait une ligne que personne ne peut administrer.
 *
 * Le mot de passe provisoire n'apparait qu'une fois, dans la reponse de
 * creation. Aucune autre route ne le rend, et il n'est stocke nulle part en
 * clair : s'il est perdu, il faut en generer un autre.
 *
 * Voir docs/adr/0014-ouverture-d-un-restaurant.md et
 * docs/adr/0015-back-office-de-plateforme-separe.md.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  CreateRestaurantResponse,
  FiscalRegime,
  Paginated,
  PaymentAccountDTO,
  PaymentAccountResponse,
  PaymentProviderMode,
  PlatformRestaurantDTO,
  ResetOwnerPasswordResponse,
} from '@scan2eat/shared';

import { ApiError, apiFetch } from '../api/client.js';
import { BrandingDialog } from '../components/BrandingDialog.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { DropdownItem, DropdownMenu } from '../components/DropdownMenu.js';
import { EmptyState } from '../components/EmptyState.js';
import { Icon } from '../components/Icon.js';
import { Modal } from '../components/Modal.js';
import { StatTile } from '../components/StatTile.js';
import { labels } from '../labels.js';
import styles from './RestaurantsPage.module.css';

interface Draft {
  name: string;
  slug: string;
  fiscalRegime: FiscalRegime;
  firstName: string;
  lastName: string;
  email: string;
}

/**
 * REEL par defaut : c'est le cas du restaurant qui a pignon sur rue, et se
 * tromper dans ce sens produit une facture avec trop de TVA -- visible, donc
 * corrigeable. L'inverse produit une facture a 0 % qui passe inapercue.
 */
const EMPTY: Draft = {
  name: '', slug: '', fiscalRegime: 'REEL', firstName: '', lastName: '', email: '',
};

/** Credentials shown once: after an opening or a password reset. */
interface Credentials {
  title: string;
  help: string;
  email: string;
  password: string;
}

type Confirming =
  | { kind: 'suspend' | 'reactivate' | 'remove'; restaurant: PlatformRestaurantDTO }
  | { kind: 'reset'; restaurant: PlatformRestaurantDTO; ownerEmail: string };

/** Slug propose a partir du nom, que l'utilisateur peut corriger. */
function toSlug(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 100);
}

export function RestaurantsPage() {
  const [restaurants, setRestaurants] = useState<PlatformRestaurantDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<PlatformRestaurantDTO | null>(null);
  const [collecting, setCollecting] = useState<PlatformRestaurantDTO | null>(null);
  // L'apparence du menu est tenue par la plateforme : voir docs/adr/0026.
  const [styling, setStyling] = useState<PlatformRestaurantDTO | null>(null);
  const [confirming, setConfirming] = useState<Confirming | null>(null);
  const [credentials, setCredentials] = useState<Credentials | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await apiFetch<Paginated<PlatformRestaurantDTO>>('/admin/restaurants');
      setRestaurants(data.items ?? []);
    } catch {
      setError(labels.restaurants.loadError);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const call = async (id: string, run: () => Promise<void>) => {
    setBusyId(id);
    setError(null);
    try {
      await run();
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : labels.auth.generic);
    } finally {
      setBusyId(null);
    }
  };

  const runConfirmed = () => {
    if (!confirming) return;
    const target = confirming;
    setConfirming(null);
    const { restaurant } = target;
    void call(restaurant.id, async () => {
      if (target.kind === 'suspend' || target.kind === 'reactivate') {
        await apiFetch(`/admin/restaurants/${restaurant.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ isActive: target.kind === 'reactivate' }),
        });
      } else if (target.kind === 'remove') {
        await apiFetch(`/admin/restaurants/${restaurant.id}`, { method: 'DELETE' });
      } else {
        const reset = await apiFetch<ResetOwnerPasswordResponse>(
          `/admin/restaurants/${restaurant.id}/owner-password`,
          { method: 'POST' },
        );
        setCredentials({
          title: labels.restaurants.resetDoneTitle(reset.owner.email),
          help: labels.restaurants.resetDoneHelp,
          email: reset.owner.email,
          password: reset.temporaryPassword,
        });
      }
    });
  };

  const active = restaurants.filter((r) => r.isActive).length;
  const totalOrders = restaurants.reduce((sum, r) => sum + r.counts.orders, 0);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{labels.restaurants.title}</h1>
          <p className="page-subtitle">{labels.restaurants.subtitle}</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn-primary" onClick={() => setCreating(true)}>
            <Icon name="plus" size={18} />
            {labels.restaurants.openTitle}
          </button>
        </div>
      </header>

      {/* Le mot de passe provisoire, visible une seule fois. */}
      {credentials && <CredentialsCard credentials={credentials} onDone={() => setCredentials(null)} />}

      {error && (
        <p role="alert" className="notice notice--danger">
          <Icon name="alert" size={18} />
          <span className="grow">{error}</span>
        </p>
      )}

      {loading ? (
        <>
          <div className="kpi-grid">
            {Array.from({ length: 4 }).map((_, i) => <div key={i} className="skeleton" style={{ height: '5.5rem', borderRadius: 'var(--radius-lg)' }} />)}
          </div>
          <div className="skeleton" style={{ height: '16rem', marginTop: 'var(--space-6)', borderRadius: 'var(--radius-lg)' }} />
        </>
      ) : restaurants.length === 0 ? (
        <div className="card">
          <EmptyState icon="store" text={labels.restaurants.empty}>
            <button type="button" className="btn-primary" onClick={() => setCreating(true)}>
              <Icon name="plus" size={18} />
              {labels.restaurants.openTitle}
            </button>
          </EmptyState>
        </div>
      ) : (
        <>
          <div className="kpi-grid">
            <StatTile icon="store" label={labels.restaurants.kpiTotal} value={restaurants.length} />
            <StatTile icon="check-circle" label={labels.restaurants.active} value={active} />
            <StatTile icon="lock" label={labels.restaurants.suspended} value={restaurants.length - active} />
            <StatTile icon="receipt" label={labels.restaurants.kpiOrders} value={totalOrders} />
          </div>

          <section className="section">
            <div className="card card--flush">
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{labels.restaurants.listTitle}</th>
                      <th>{labels.restaurants.owner}</th>
                      <th className="num">{labels.restaurants.tablesColumn}</th>
                      <th className="num">{labels.restaurants.ordersColumn}</th>
                      <th>{labels.restaurants.settingsColumn}</th>
                      <th>{labels.restaurants.statusColumn}</th>
                      <th><span className="visually-hidden">{labels.common.more}</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {restaurants.map((r) => (
                      <tr key={r.id} className={r.isActive ? undefined : styles.suspendedRow}>
                        <td>
                          <div className="row" style={{ gap: 'var(--space-3)' }}>
                            <span className="avatar avatar--soft">{r.name.charAt(0)}</span>
                            <span style={{ display: 'grid', minWidth: 0 }}>
                              <strong className="truncate">{r.name}</strong>
                              <span className="mono text-xs text-muted">{r.slug}</span>
                            </span>
                          </div>
                        </td>
                        <td className="text-sm">
                          {r.owner ? r.owner.email : <span className="text-muted">{labels.restaurants.noOwner}</span>}
                        </td>
                        <td className="num">{r.counts.tables}</td>
                        <td className="num">{r.counts.orders}</td>
                        <td className="text-sm text-muted">
                          {r.currency} · {labels.restaurants.taxRate((r.taxRatePermille / 10).toFixed(1))}
                          {' · '}
                          {labels.restaurants.regimeShort[r.fiscalRegime]}
                        </td>
                        <td>
                          <span className={`badge badge--dot ${r.isActive ? 'badge--ready' : 'badge--neutral'}`}>
                            {r.isActive ? labels.restaurants.active : labels.restaurants.suspended}
                          </span>
                        </td>
                        <td className="num">
                          <RowMenu
                            restaurant={r}
                            busy={busyId === r.id}
                            onEdit={() => setEditing(r)}
                            onPayment={() => setCollecting(r)}
                            onBranding={() => setStyling(r)}
                            onConfirm={setConfirming}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <p className={styles.footnote}>
              <Icon name="info" size={16} />
              {labels.restaurants.cannotRemove} {labels.restaurants.removeHint}
            </p>
          </section>
        </>
      )}

      {creating && (
        <OpenRestaurantDialog
          onClose={() => setCreating(false)}
          onOpened={async (result) => {
            setCreating(false);
            setCredentials({
              title: labels.restaurants.openedTitle(result.restaurant.name),
              help: labels.restaurants.openedHelp,
              email: result.owner.email,
              password: result.temporaryPassword,
            });
            await load();
          }}
        />
      )}

      {editing && (
        <EditRestaurantDialog
          restaurant={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}

      {collecting && (
        <PaymentAccountDialog restaurant={collecting} onClose={() => setCollecting(null)} />
      )}

      {styling && (
        <BrandingDialog
          restaurant={styling}
          onClose={() => setStyling(null)}
          onChanged={load}
        />
      )}

      {confirming && (
        <ConfirmDialog
          title={
            confirming.kind === 'suspend' ? labels.restaurants.suspendTitle(confirming.restaurant.name)
              : confirming.kind === 'reactivate' ? labels.restaurants.confirmReactivate(confirming.restaurant.name)
                : confirming.kind === 'remove' ? labels.restaurants.removeTitle(confirming.restaurant.name)
                  : labels.restaurants.resetTitle('ownerEmail' in confirming ? confirming.ownerEmail : '')
          }
          message={
            confirming.kind === 'suspend' ? labels.restaurants.suspendHelp
              : confirming.kind === 'reactivate' ? undefined
                : confirming.kind === 'remove' ? labels.restaurants.removeHelp
                  : labels.restaurants.resetHelp
          }
          confirmLabel={
            confirming.kind === 'suspend' ? labels.restaurants.suspend
              : confirming.kind === 'reactivate' ? labels.restaurants.reactivate
                : confirming.kind === 'remove' ? labels.restaurants.remove
                  : labels.restaurants.resetPassword
          }
          destructive={confirming.kind !== 'reactivate'}
          onConfirm={runConfirmed}
          onCancel={() => setConfirming(null)}
        />
      )}
    </div>
  );
}

/* -- Credentials, shown once -- */

function CredentialsCard({ credentials, onDone }: { credentials: Credentials; onDone: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <section role="status" className={styles.credentials}>
      <span className="icon-circle"><Icon name="lock" size={22} /></span>
      <div className="grow">
        <h2 className={styles.credentialsTitle}>{credentials.title}</h2>
        <p className={styles.credentialsHelp}>{credentials.help}</p>
        <dl className={styles.credentialsList}>
          <div>
            <dt>{labels.restaurants.ownerEmail}</dt>
            <dd>{credentials.email}</dd>
          </div>
          <div>
            <dt>{labels.restaurants.temporaryPassword}</dt>
            <dd className={styles.password}>
              <span className="mono">{credentials.password}</span>
              <button
                type="button"
                className="btn-ghost btn-sm"
                onClick={() => {
                  navigator.clipboard?.writeText(credentials.password).then(
                    () => { setCopied(true); window.setTimeout(() => setCopied(false), 2500); },
                    () => undefined,
                  );
                }}
              >
                <Icon name={copied ? 'check' : 'copy'} size={16} />
                {copied ? labels.restaurants.copied : labels.restaurants.copy}
              </button>
            </dd>
          </div>
        </dl>
      </div>
      <button type="button" className="btn-primary" onClick={onDone}>{labels.restaurants.noted}</button>
    </section>
  );
}

/* -- Row actions -- */

function RowMenu({ restaurant, busy, onEdit, onPayment, onBranding, onConfirm }: {
  restaurant: PlatformRestaurantDTO;
  busy: boolean;
  onEdit: () => void;
  onPayment: () => void;
  onBranding: () => void;
  onConfirm: (c: Confirming) => void;
}) {
  const anchor = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  const owner = restaurant.owner;
  // Seules les commandes retiennent la suppression : elles portent les factures.
  const removable = restaurant.counts.orders === 0;

  return (
    <>
      <button
        ref={anchor}
        type="button"
        className="btn-icon btn-icon--quiet btn-icon--sm"
        aria-label={labels.restaurants.actionsFor(restaurant.name)}
        aria-expanded={open}
        aria-haspopup="true"
        disabled={busy}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="more" size={18} />
      </button>
      <DropdownMenu open={open} onClose={close} anchorRef={anchor}>
        <DropdownItem onClick={() => { close(); onEdit(); }} icon={<Icon name="edit" size={18} />}>
          {labels.restaurants.edit}
        </DropdownItem>
        <DropdownItem onClick={() => { close(); onPayment(); }} icon={<Icon name="cash" size={18} />}>
          {labels.restaurants.paymentTitle}
        </DropdownItem>
        <DropdownItem onClick={() => { close(); onBranding(); }} icon={<Icon name="image" size={18} />}>
          {labels.branding.title}
        </DropdownItem>
        {owner && (
          <DropdownItem
            onClick={() => { close(); onConfirm({ kind: 'reset', restaurant, ownerEmail: owner.email }); }}
            icon={<Icon name="lock" size={18} />}
          >
            {labels.restaurants.resetPassword}
          </DropdownItem>
        )}
        <DropdownItem
          onClick={() => { close(); onConfirm({ kind: restaurant.isActive ? 'suspend' : 'reactivate', restaurant }); }}
          icon={<Icon name={restaurant.isActive ? 'close' : 'refresh'} size={18} />}
        >
          {restaurant.isActive ? labels.restaurants.suspend : labels.restaurants.reactivate}
        </DropdownItem>
        <div className="dropdown-separator" />
        {removable ? (
          <DropdownItem onClick={() => { close(); onConfirm({ kind: 'remove', restaurant }); }} icon={<Icon name="trash" size={18} />} danger>
            {labels.restaurants.remove}
          </DropdownItem>
        ) : (
          <p className="dropdown-label" style={{ maxWidth: '16rem', margin: 0 }}>{labels.restaurants.cannotRemove}</p>
        )}
      </DropdownMenu>
    </>
  );
}

/* -- Dialogs -- */

function OpenRestaurantDialog({ onClose, onOpened }: {
  onClose: () => void;
  onOpened: (result: CreateRestaurantResponse) => Promise<void>;
}) {
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const slug = slugTouched ? draft.slug : toSlug(draft.name);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const result = await apiFetch<CreateRestaurantResponse>('/admin/restaurants', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: draft.name.trim(),
          slug: slug.trim(),
          fiscalRegime: draft.fiscalRegime,
          owner: {
            firstName: draft.firstName.trim(),
            lastName: draft.lastName.trim(),
            email: draft.email.trim(),
          },
        }),
      });
      await onOpened(result);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : labels.auth.generic);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={labels.restaurants.openTitle}
      description={labels.restaurants.openHelp}
      size="wide"
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      locked={saving}
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose} disabled={saving}>{labels.restaurants.cancel}</button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? labels.restaurants.opening : labels.restaurants.open}
          </button>
        </>
      }
    >
      <div className="form-grid">
        {error && <p role="alert" className="notice notice--danger" style={{ margin: 0 }}><Icon name="alert" size={18} />{error}</p>}

        <section className={styles.fieldset}>
          <h3 className={styles.legend}>{labels.restaurants.restaurantSection}</h3>
          <div className="form-grid form-grid--2">
            <Field id="r-name" label={labels.restaurants.name} value={draft.name} required autoFocus
              onChange={(v) => setDraft({ ...draft, name: v })} />
            <Field id="r-slug" label={labels.restaurants.slug} value={slug} hint={labels.restaurants.slugHint} required mono
              onChange={(v) => { setSlugTouched(true); setDraft({ ...draft, slug: v }); }} />
          </div>
          <div style={{ marginTop: 'var(--space-4)' }}>
            <RegimeField id="r-regime" value={draft.fiscalRegime} hint={labels.restaurants.regimeHint}
              onChange={(v) => setDraft({ ...draft, fiscalRegime: v })} />
          </div>
        </section>

        <section className={styles.fieldset}>
          <h3 className={styles.legend}>{labels.restaurants.ownerSection}</h3>
          <p className="field-hint" style={{ marginBottom: 'var(--space-3)' }}>{labels.restaurants.ownerHint}</p>
          <div className="form-grid form-grid--2">
            <Field id="o-first" label={labels.restaurants.firstName} value={draft.firstName} required
              onChange={(v) => setDraft({ ...draft, firstName: v })} />
            <Field id="o-last" label={labels.restaurants.lastName} value={draft.lastName} required
              onChange={(v) => setDraft({ ...draft, lastName: v })} />
          </div>
          <div style={{ marginTop: 'var(--space-4)' }}>
            <Field id="o-email" label={labels.restaurants.ownerEmail} value={draft.email} type="email" required
              onChange={(v) => setDraft({ ...draft, email: v })} />
          </div>
        </section>
      </div>
    </Modal>
  );
}

function EditRestaurantDialog({ restaurant, onClose, onSaved }: {
  restaurant: PlatformRestaurantDTO;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [draft, setDraft] = useState({
    name: restaurant.name,
    address: restaurant.address ?? '',
    phone: restaurant.phone ?? '',
    fiscalRegime: restaurant.fiscalRegime,
    taxRatePermille: String(restaurant.taxRatePermille),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await apiFetch(`/admin/restaurants/${restaurant.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: draft.name.trim(),
          address: draft.address.trim() || null,
          phone: draft.phone.trim() || null,
          fiscalRegime: draft.fiscalRegime,
          // Sous TPS le taux ne se discute pas. L'envoyer explicitement plutot
          // que de compter sur le serveur pour le corriger : l'ecran montre 0,
          // l'appel dit 0, et les deux disent la meme chose.
          taxRatePermille: draft.fiscalRegime === 'TPS' ? 0 : Number(draft.taxRatePermille),
        }),
      });
      await onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : labels.auth.generic);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={labels.restaurants.editTitle}
      description={restaurant.slug}
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      locked={saving}
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose} disabled={saving}>{labels.restaurants.cancel}</button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? labels.restaurants.saving : labels.restaurants.save}
          </button>
        </>
      }
    >
      <div className="form-grid">
        {error && <p role="alert" className="notice notice--danger" style={{ margin: 0 }}><Icon name="alert" size={18} />{error}</p>}
        <Field id="e-name" label={labels.restaurants.name} value={draft.name} required autoFocus
          onChange={(v) => setDraft({ ...draft, name: v })} />
        <Field id="e-address" label={labels.restaurants.address} value={draft.address}
          onChange={(v) => setDraft({ ...draft, address: v })} />
        <div className="form-grid form-grid--2">
          <Field id="e-phone" label={labels.restaurants.phone} value={draft.phone}
            onChange={(v) => setDraft({ ...draft, phone: v })} />
          <Field id="e-tax" label={labels.restaurants.taxPermille} value={draft.taxRatePermille}
            disabled={draft.fiscalRegime === 'TPS'}
            hint={draft.fiscalRegime === 'TPS' ? labels.restaurants.regimeTpsLocked : labels.restaurants.taxHint}
            onChange={(v) => setDraft({ ...draft, taxRatePermille: v })} />
        </div>
        <RegimeField
          id="e-regime"
          value={draft.fiscalRegime}
          onChange={(v) => setDraft({
            ...draft,
            fiscalRegime: v,
            // Basculer au TPS met le taux a 0 sous les yeux de l'operateur.
            // Le laisser a 180 en grise ferait lire au formulaire l'inverse
            // de ce qu'il enverra.
            taxRatePermille: v === 'TPS' ? '0' : draft.taxRatePermille,
          })} />
      </div>
    </Modal>
  );
}

function Field({ id, label, value, onChange, required, type, hint, autoFocus, mono, disabled }: {
  id: string; label: string; value: string; onChange: (v: string) => void;
  required?: boolean; type?: string; hint?: string; autoFocus?: boolean; mono?: boolean;
  disabled?: boolean;
}) {
  return (
    <div className="field">
      <label htmlFor={id} className="field-label">{label}</label>
      <input
        id={id}
        className={`input ${mono ? 'mono' : ''}`}
        type={type ?? 'text'}
        value={value}
        required={required}
        autoFocus={autoFocus}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
      {hint && <p className="field-hint">{hint}</p>}
    </div>
  );
}

/**
 * Choix du regime fiscal.
 *
 * Une liste de deux valeurs plutot qu'une case a cocher : « cocher pour TPS »
 * laisserait croire a une option, alors que c'est une declaration fiscale dont
 * les deux branches se valent et doivent etre lues avant de choisir.
 */
function RegimeField({ id, value, onChange, hint }: {
  id: string; value: FiscalRegime; onChange: (v: FiscalRegime) => void; hint?: string;
}) {
  return (
    <div className="field">
      <label htmlFor={id} className="field-label">{labels.restaurants.regime}</label>
      <select
        id={id}
        className="input"
        value={value}
        onChange={(e) => onChange(e.target.value as FiscalRegime)}
      >
        <option value="REEL">{labels.restaurants.regimeReel}</option>
        <option value="TPS">{labels.restaurants.regimeTps}</option>
      </select>
      {hint && <p className="field-hint">{hint}</p>}
    </div>
  );
}

/**
 * Depot des cles PayDunya d'un restaurant.
 *
 * C'est Scan2Eat qui les saisit, a partir de ce que le restaurateur nous a
 * transmis : manipuler des cles d'API n'est pas son metier, et ce pouvoir n'a
 * rien a faire dans l'application de salle. Voir docs/adr/0019.
 */
function PaymentAccountDialog({ restaurant, onClose }: {
  restaurant: PlatformRestaurantDTO;
  onClose: () => void;
}) {
  const [account, setAccount] = useState<PaymentAccountDTO | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<PaymentProviderMode>('TEST');
  const [keys, setKeys] = useState({ masterKey: '', privateKey: '', token: '' });

  const path = `/admin/restaurants/${restaurant.id}/payment-account`;

  const load = useCallback(async () => {
    try {
      const data = await apiFetch<PaymentAccountResponse>(path);
      setAccount(data.account);
      setMode(data.account?.mode ?? 'TEST');
    } catch {
      setAccount(null);
    } finally {
      setLoaded(true);
    }
  }, [path]);

  useEffect(() => { void load(); }, [load]);

  const complete = Boolean(keys.masterKey && keys.privateKey && keys.token);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await apiFetch<PaymentAccountResponse>(path, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode, ...keys }),
      });
      setAccount(data.account);
      setKeys({ masterKey: '', privateKey: '', token: '' });
      setEditing(false);
    } catch (err) {
      // Le serveur eprouve les cles avant d'ecrire : un refus dit qu'elles ne
      // fonctionnent pas, pas que la saisie est mal formee.
      const code = err instanceof ApiError ? err.code : null;
      // Le prestataire dit precisement ce qui ne va pas -- « Invalid Masterkey
      // Specified », par exemple. Le remplacer par notre propre phrase obligerait
      // a deviner.
      const providerMessage =
        err instanceof ApiError
          ? (err.details as { providerMessage?: string } | null)?.providerMessage
          : undefined;
      setError(
        code === 'PAYMENT_PROVIDER_REJECTED'
          ? `${labels.restaurants.paymentRejected}${providerMessage ? ` (${providerMessage})` : ''}`
        : code === 'PAYMENT_PROVIDER_UNAVAILABLE' ? labels.restaurants.paymentUnavailable
        : err instanceof ApiError ? err.message
        : labels.auth.generic,
      );
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await apiFetch(path, { method: 'DELETE' });
      setAccount(null);
      setConfirmingRemove(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : labels.auth.generic);
    } finally {
      setBusy(false);
    }
  };

  const cancelEdit = () => { setEditing(false); setError(null); };

  return (
    <Modal
      title={labels.restaurants.paymentTitle}
      description={restaurant.name}
      size="wide"
      onClose={onClose}
      locked={busy}
      onSubmit={(e) => {
        e.preventDefault();
        if (editing && complete && !busy) void save();
      }}
      footer={
        editing ? (
          <>
            <button type="button" className="btn-ghost" onClick={cancelEdit} disabled={busy}>
              {labels.restaurants.cancel}
            </button>
            <button type="submit" className="btn-primary" disabled={!complete || busy}>
              {busy ? labels.restaurants.paymentChecking : labels.restaurants.paymentSave}
            </button>
          </>
        ) : confirmingRemove ? (
          <>
            <button type="button" className="btn-ghost" onClick={() => setConfirmingRemove(false)} disabled={busy}>
              {labels.restaurants.cancel}
            </button>
            <button type="button" className="btn-danger btn-danger--solid" onClick={() => void remove()} disabled={busy}>
              {labels.restaurants.paymentRemove}
            </button>
          </>
        ) : (
          <>
            {account && (
              <button type="button" className="btn-danger" onClick={() => setConfirmingRemove(true)} disabled={busy}>
                {labels.restaurants.paymentRemove}
              </button>
            )}
            <button type="button" className="btn-primary" onClick={() => setEditing(true)} disabled={!loaded}>
              {account ? labels.restaurants.paymentReplace : labels.restaurants.paymentConfigure}
            </button>
          </>
        )
      }
    >
      <div className="form-grid">
        {error && <p role="alert" className="notice notice--danger" style={{ margin: 0 }}><Icon name="alert" size={18} />{error}</p>}

        {!loaded ? (
          <div className="skeleton" style={{ height: '3rem', borderRadius: 'var(--radius)' }} />
        ) : account ? (
          <p className="field-hint" style={{ margin: 0 }}>
            {labels.restaurants.paymentConfigured} :{' '}
            <span className="mono">
              ...{account.keyFingerprints.masterKey} · ...{account.keyFingerprints.privateKey} · ...
              {account.keyFingerprints.token}
            </span>
            {' — '}
            {account.mode === 'LIVE'
              ? labels.restaurants.paymentModeLive
              : labels.restaurants.paymentModeTest}
          </p>
        ) : (
          <p className="field-hint" style={{ margin: 0 }}>{labels.restaurants.paymentNone}</p>
        )}

        {confirmingRemove && (
          <p role="alert" className="notice notice--warning" style={{ margin: 0 }}>
            <Icon name="alert" size={18} />
            {labels.restaurants.paymentRemoveConfirm(restaurant.name)}
          </p>
        )}

        {editing && (
          <>
            <div className="field">
              <span className="field-label" id="payment-mode-label">{labels.restaurants.paymentMode}</span>
              <div className="row" role="radiogroup" aria-labelledby="payment-mode-label" style={{ gap: 'var(--space-2)' }}>
                {(['TEST', 'LIVE'] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    role="radio"
                    aria-checked={mode === m}
                    onClick={() => setMode(m)}
                    className={`chip ${mode === m ? 'chip--active' : ''}`}
                  >
                    {m === 'LIVE'
                      ? labels.restaurants.paymentModeLive
                      : labels.restaurants.paymentModeTest}
                  </button>
                ))}
              </div>
              <p className="field-hint">{labels.restaurants.paymentModeHint}</p>
            </div>

            <p className="field-hint" style={{ margin: 0 }}>{labels.restaurants.paymentKeysHint}</p>

            <Field id="p-master" label={labels.restaurants.paymentMasterKey} value={keys.masterKey} mono
              onChange={(v) => setKeys({ ...keys, masterKey: v.trim() })} />
            <Field id="p-private" label={labels.restaurants.paymentPrivateKey} value={keys.privateKey} mono
              onChange={(v) => setKeys({ ...keys, privateKey: v.trim() })} />
            <Field id="p-token" label={labels.restaurants.paymentToken} value={keys.token} mono
              onChange={(v) => setKeys({ ...keys, token: v.trim() })} />
          </>
        )}
      </div>
    </Modal>
  );
}
